import {afterNextRender, booleanAttribute, Component, EventEmitter, inject, Injector, Input, numberAttribute, OnInit, Output, ViewChild, AfterViewInit} from '@angular/core';
import {CellClickEvent, ColumnBase, ColumnReorderEvent, ColumnResizeArgs, ColumnVisibilityChangeEvent, GridComponent, GroupableSettings, GroupRowArgs, KENDO_GRID, MultipleSortSettings, PageChangeEvent, SelectableMode} from '@progress/kendo-angular-grid';
import {CompositeFilterDescriptor, FilterDescriptor, GroupDescriptor, SortDescriptor} from '@progress/kendo-data-query';

import {GrilleBindingDirective} from './grille-binding.directive';
import {DonneesColonneBoutonIconeClick, DescriptionFiltre, DonneesColonne, DonneesGrilleSelection, GroupeColonne, TailleColonne, VisibiliteColonne} from './DonneesGrille';
import {TexteFiltreRapideComponent} from "./filtres-rapides/texte-filtre-rapide.component";
import {DateFiltreRapideComponent} from "./filtres-rapides/date-filtre-rapide.component";
import {BooleanFiltreRapideComponent} from './filtres-rapides/boolean-filtre-rapide.component';
import {NumeriqueFiltreRapideComponent} from "./filtres-rapides/numerique-filtre-rapide.component";
import {TexteCouleurColonneComponent} from "./colonnes/texte-couleur-colonne/texte-couleur-colonne.component";
import {FonteIconeComponent} from '../fonte-icone/fonte-icone.component';
import {langueCourante, ValLangueService} from '../../../../../val-definitions-communes-lib/src/public-api';


//Composant grille
@Component (
{
  selector: 'val-grille',
  imports: 
  [
    KENDO_GRID,
    GrilleBindingDirective,
    TexteFiltreRapideComponent,
    DateFiltreRapideComponent,
    BooleanFiltreRapideComponent,
    NumeriqueFiltreRapideComponent,
    TexteCouleurColonneComponent,
    FonteIconeComponent
  ],
  templateUrl: './grille.component.html',
  styleUrl: './grille.component.scss'
})
export class GrilleComponent implements OnInit, AfterViewInit
{
  //Champ de la ligne portant la couleur des cellules au format CUR_COLOR.
  //  Convention partagée avec le paramétrage de recherche : une colonne technique au format HID,
  //  nommée COULEUR_LIGNE, calcule la couleur en SQL. La règle métier reste donc en base, et la
  //  valeur du montant reste un décimal — c'est ce qui préserve le filtre, le tri et l'export.
  public static readonly champCouleurLigne: string = "COULEUR_LIGNE";

  private valLangueService = inject (ValLangueService);

  //Texte d’interface traduit, ou texte d’origine si l’application ne porte pas la clé grille.<cle>
  //  Paramètres
  //    cle    : Clé sous grille.
  //    defaut : Texte d’origine
  public Texte (cle: string, defaut: string) : string
  {
    return this.valLangueService.TraduireOuDefaut ("grille." + cle, defaut);
  }

  //Création
  //  Paramètres
  //    injector : Injecteur
  constructor (private injector: Injector)
  {
    this.colonnes = [];
    this.menuColonne = true;
    this.donnees = [];
    this.taillePage = 0;
    this.champSelection = "";
    this.groupable = false;
    this.filtrable = true;
    this.filtrageServeur = false;
    this.simpleSelection = false;
    this.colonneSelection = true;
    this.modeSelection = "multiple";
    this.numeroPageEnCours = 0;
    this.skip = 0;
    this.groupes = [];
    this.pageable = false;
    this.chargementEnCours = false;

    this.donneesSuivante = new EventEmitter<number> ();
    this.colonneBoutonIconeClick = new EventEmitter<DonneesColonneBoutonIconeClick> ();
    this.selectionChange = new EventEmitter<DonneesGrilleSelection> ();
    this.doubleClick = new EventEmitter<void>;
    this.trisChange = new EventEmitter<SortDescriptor[]>;
    this.filtresChange = new EventEmitter<DescriptionFiltre[]> ();
    this.ordresChange = new EventEmitter<string[]> ();
    this.visibiliteColonnesChange = new EventEmitter<VisibiliteColonne[]>;
    this.tailleColonneChange = new EventEmitter<TailleColonne>;
    this.groupeColonnesChange = new EventEmitter<GroupeColonne[]>;
    this.donneesSelection = new DonneesGrilleSelection ();

    this.groupableSettings = 
    {
      enabled: false,
      emptyText: "Faites glisser un en-tête de colonne et déposer ici pour grouper par cette colonne",
      showFooter: false
    };

    this.sortSettings =
    {
      mode: "multiple",
      initialDirection: "asc",
      allowUnsort: true,
      showIndexes: true
    };

    this.sort = [];

    this.filtres = 
    {
      logic: "and",
      filters: []
    };

    this.filtresEnCours = [];
    this.descriptionsFiltres = [];
  }

  //Initialisation
  public ngOnInit (): void 
  {
    this.groupableSettings.enabled = this.groupable;

    this.modeSelection = this.simpleSelection ? "single" : "multiple";
  }

  //Après initialisation de la vue
  public ngAfterViewInit (): void
  {
    // Bug Kendo ? Si on renseigne dans le template [isGroupExpanded]="GroupeToujourOuvert", la zone de drop du regroupement est toujours visible même si "groupable" est à false. 
    // En attendant la correction du bug, on force la mise à jour de l'état de l'affichage de la zone de drop du regroupement après le rendu de la grille.
    if (this.groupable)
      this.grille.isGroupExpanded = this.GroupeToujourOuvert;
  }

  //Grille
  @ViewChild ("grille")
  public grille!: GridComponent;

  //Colonnes
  @Input ()
  public colonnes: DonneesColonne[];

  //Indique si on affiche le menu des colonnes 
  @Input ({transform: booleanAttribute})
  public menuColonne: boolean;

  //Données
  @Input ()
  public donnees: any[];

  //Taille d’une page
  @Input ({transform: numberAttribute})
  public taillePage: number;

  //Champ de sélection
  @Input ()
  public champSelection: string;

  //Indique si regroupement
  @Input ({transform: booleanAttribute})
  public groupable: boolean;

  //Indique si filtrable
  @Input ({transform: booleanAttribute})
  public filtrable: boolean;

  //Indique si les données reçues sont déjà filtrées par le serveur : la grille ne les refiltre pas
  @Input ({transform: booleanAttribute})
  public filtrageServeur: boolean;

  //Indique si simple sélection
  @Input ({transform: booleanAttribute})
  public simpleSelection: boolean;

  //Indique si on affiche la colonne de sélection
  @Input ({transform: booleanAttribute})
  public colonneSelection: boolean;

  //Indique si pageable
  @Input ({transform: booleanAttribute})
  public pageable: boolean;

  //Indique si un chargement est en cours : la grille vide n’annonce alors pas « aucun enregistrement »
  @Input ({transform: booleanAttribute})
  public chargementEnCours: boolean;

  //Evénement de demande des données suivante
  @Output ()
  public donneesSuivante: EventEmitter<number>;

  //Evénement de clic sur une colonne BTN_ICON
  @Output ()
  public colonneBoutonIconeClick: EventEmitter<DonneesColonneBoutonIconeClick>;

  //Evénement de changement de sélection
  @Output ()
  public selectionChange: EventEmitter<DonneesGrilleSelection>;

  //Evénement double click
  @Output ()
  public doubleClick: EventEmitter<void>;

  //Evénement de changement de tris
  @Output ()
  public trisChange: EventEmitter<SortDescriptor[]>;

  //Evénement de changement de filtres
  @Output ()
  public filtresChange: EventEmitter<DescriptionFiltre[]>;

  //Evénement de changement d’ordres
  @Output ()
  public ordresChange: EventEmitter<string[]>;

  //Evénement de chagement de visibilité des colonnes
  @Output ()
  public visibiliteColonnesChange: EventEmitter<VisibiliteColonne[]>;

  //Evénement de changement de taille d’une colonne
  @Output ()
  public tailleColonneChange: EventEmitter<TailleColonne>;

  //Evénement de changement de groupe des colonnes
  @Output ()
  public groupeColonnesChange: EventEmitter<GroupeColonne[]>;

  //Mode de sélection
  public modeSelection: SelectableMode;

  //Numéro de page en cours
  private numeroPageEnCours: number;

  //Index de la première ligne affichée (pager Kendo)
  public skip: number;

  //Données de sélection
  public donneesSelection: DonneesGrilleSelection;

  //Paramètres du regroupement
  public groupableSettings: GroupableSettings;

  //Paramètres du trie
  public sortSettings: MultipleSortSettings;

  //Description des tris
  public sort: SortDescriptor[];

  //Description du regroupement
  public groupes: GroupDescriptor[];

  //Description des filtres
  public descriptionsFiltres: DescriptionFiltre[];

  //Filtres
  public filtres: CompositeFilterDescriptor;

  //Filtres en cours
  private filtresEnCours: FilterDescriptor[];

  //Retourne la classe CSS pour une ligne sélectionnée
  public RetournerCssClassLigneFocus = (donneesLigne: any) => (
  {
    "val-grille-focus": donneesLigne.dataItem[this.champSelection] === this.donneesSelection.identifiantFocus
  });

  //Force l’ouverture des groupes
  //  Paramètres
  //    groupRowArgs : Arguments
  //  Retourne
  //    Toujour vrai
  public GroupeToujourOuvert (groupRowArgs: GroupRowArgs) : boolean
  {
    return true;
  }

  //Formate un montant avec signe "+" si positif
  public FormatMonnaieAvecSigne(valeur: number): string {
    const formatted = Math.abs(valeur).toLocaleString(langueCourante.locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
    if (valeur > 0) return '+' + formatted;
    if (valeur < 0) return '-' + formatted;
    return formatted;
  }

  //Formate un montant en euros avec séparateur de milliers et deux décimales
  public FormatMonnaie(valeur: number): string {
    return valeur == null ? '' : valeur.toLocaleString(langueCourante.locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
  }

  //Formate un nombre entier avec séparateur de milliers
  public FormatEntier(valeur: number): string {
    return valeur == null ? '' : valeur.toLocaleString(langueCourante.locale);
  }

  //Formate un nombre décimal avec séparateur de milliers et deux décimales
  public FormatDecimal(valeur: number): string {
    return valeur == null ? '' : valeur.toLocaleString(langueCourante.locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  //Retourne la couleur d'une cellule de montant au format CUR_COLOR
  //  La couleur n'est pas portée par la valeur : un montant doit rester un décimal, sans quoi le
  //  filtre numérique, le tri numérique et le style décimal de l'export Excel se retrouvent cassés.
  //  Elle est lue sur un champ distinct de la ligne, alimenté par une colonne technique de la
  //  recherche (cf. champCouleurLigne).
  //  Paramètres
  //    ligne : Données de la ligne
  //  Retourne
  //    Code couleur CSS, ou chaîne vide si la ligne n'en porte pas
  public RetournerCouleurMontant (ligne: any) : string
  {
    let couleur: unknown;

    if (!ligne)
      return '';

    couleur = ligne[GrilleComponent.champCouleurLigne];

    return typeof couleur === 'string' ? couleur : '';
  }

  //Réinitialise la grille
  public Reinitialiser () : void
  {
    this.numeroPageEnCours = 0;
    this.skip = 0;
    this.donneesSelection.identifiantFocus = null;
    this.donneesSelection.identifiantsSelectionnes = [];
    this.EnvoyerSelectionChange ();
  }

  //Réinitialise les filtres et le tri sans émettre d'événements
  public ReinitialiserFiltresEtTri () : void
  {
    this.filtres = {logic: "and", filters: []};
    this.filtresEnCours = [];
    this.descriptionsFiltres = [];
    this.sort = [];
  }

  //Traite le changement de page
  public PageChange (event: PageChangeEvent) : void
  {
    this.skip = event.skip;
  }

  //Demande les données suivantes
  public DemanderDonneesSuivante () : void 
  {
    let numeroPage: number;
    let resteDonnees: number;

    if (this.pageable)
      return;

    numeroPage = Math.floor (this.donnees.length / this.taillePage) + 1;
    resteDonnees = this.donnees.length % this.taillePage;

    if (this.numeroPageEnCours !== numeroPage && resteDonnees === 0)
    {
      this.numeroPageEnCours = numeroPage;

      this.donneesSuivante.emit (numeroPage);
    }
  }

  //Traite le changement de sélection
  public SelectionChange () : void 
  {
    this.EnvoyerSelectionChange ();
  }

  //Traite le click sur une cellule
  //  Paramètres
  //    cellClickEvent : Données de l’événement
  public CelluleClick (cellClickEvent: CellClickEvent) : void 
  {
    this.donneesSelection.identifiantFocus = cellClickEvent.dataItem[this.champSelection];
    this.EnvoyerSelectionChange ();
  }

  //Envoie le changement de sélection
  private EnvoyerSelectionChange () : void 
  {
    this.selectionChange.emit (this.donneesSelection);
  }

  //Traite le double click
  public DoubleClick () : void 
  {
    this.doubleClick.emit ();
  }

  //Traite le clic sur l'icone d'une colonne BTN_ICON
  //  Paramètres
  //    dataItem : Données de la ligne
  //    champ    : Champ de la colonne
  //    event    : Événement de clic
  public ColonneBoutonIconeClick (dataItem: any, champ: string, event: MouseEvent) : void 
  {
    event.stopPropagation (); // Empêche le déclenchement de la sélection de la ligne

    let donnees: DonneesColonneBoutonIconeClick;

    donnees = new DonneesColonneBoutonIconeClick ();
    donnees.identifiant = dataItem[this.champSelection];
    donnees.champ = champ;

    this.colonneBoutonIconeClick.emit (donnees);
  }

  //Traite le changement de tris
  //  Paramètres
  //    sort : Description des tris
  public TrisChange (sort: SortDescriptor[]) : void 
  {
    this.sort = sort.filter (srt => srt.dir !== undefined);
    
    this.trisChange.emit (sort);
  }

  //Traite le changement de filtres
  //  Paramètres
  //    filter : Descriptions des filtres
  public FiltresChange (filter: CompositeFilterDescriptor) : void 
  {
    let mettreAJour: boolean;
    let descriptionFiltre: FilterDescriptor;

    mettreAJour = this.ControlerChangementFiltres (filter.filters as FilterDescriptor[]);

    this.filtres = filter;

    this.MettreAJourFiltresEnCours ();

    if (mettreAJour)
    {
      this.CreerDescriptionsFiltres ();
      this.filtresChange.emit (this.descriptionsFiltres);
    }
  }

  //Met à jour les filtres en cours
  public MettreAJourFiltresEnCours () : void 
  {
    let descriptionFiltre: FilterDescriptor;

    this.filtresEnCours = this.filtres.filters.map ((fd) =>
    {
      descriptionFiltre =
      {
        field: (fd as FilterDescriptor).field,
        operator: (fd as FilterDescriptor).operator,
        value: (fd as FilterDescriptor).value
      };

      return descriptionFiltre;
    });
  }

  //Contrôle si les filtres on changés
  //  Paramètres
  //    nouveauFiltres : Nouveaux filtres
  //  Retourne vrai si les filtres ont changés
  private ControlerChangementFiltres (nouveauFiltres: FilterDescriptor[]) : boolean 
  {
    let changement: boolean;

    if (this.filtresEnCours.length !== nouveauFiltres.length)
      changement = true;
    else
    {
      changement = false;

      nouveauFiltres.forEach ((nouveauFiltre: FilterDescriptor) =>
      {
        if (this.filtresEnCours.findIndex ((fil) =>
            {
              return fil.field === nouveauFiltre.field &&
                     fil.operator === nouveauFiltre.operator &&
                     fil.value === nouveauFiltre.value;
            }) === -1)
        {
          changement = true;
        }

      });
    }

    return changement;
  }

  //Crée les descriptions des filtres
  public CreerDescriptionsFiltres () : void 
  {
    let donneesColonne: DonneesColonne | undefined;
    let descriptionFiltre: DescriptionFiltre;

    this.descriptionsFiltres = [];

    this.filtresEnCours.forEach ((filtre: FilterDescriptor) =>
    {
      donneesColonne = this.colonnes.find ((col) =>
                       {
                         return col.champ === filtre.field;
                       });

      if (donneesColonne)
      {
        descriptionFiltre = new DescriptionFiltre ();
        descriptionFiltre.champ = donneesColonne.champ;
        descriptionFiltre.operateur = filtre.operator as string;

        switch (donneesColonne.format)
        {
          case "TXT" :
          case "TXT_COLOR" : descriptionFiltre.valeurTexte = filtre.value;    break;
          case "INT" : descriptionFiltre.valeurInt = filtre.value;      break;
          case "DAT" :
          case "DAT_H" : descriptionFiltre.valeurDate = filtre.value;    break;
          case "CUR" :
          case "CUR_COLOR" :
          case "CUR_SIGN" :
          case "DEC" : descriptionFiltre.valeurDecimale = filtre.value; break;
          case "CHK" : descriptionFiltre.valeurBoolean = filtre.value;  break;
        }

        this.descriptionsFiltres.push (descriptionFiltre);
      }
    });
  }

  //L'ordre des colonnes change
  //  Paramètres
  //    evenement : Evénement
  public OrdreColonneChange (evenement: ColumnReorderEvent) : void 
  {
    let donneesColonne: DonneesColonne | undefined;
    let identifiantsColonnes: string[];

    afterNextRender (() =>
    {
      identifiantsColonnes = this.grille.columnList.toArray ().sort ((colA: ColumnBase, colB: ColumnBase) =>
      {
        let resultat: number;

        resultat = 0;
                  
        if (colA.orderIndex < colB.orderIndex)
          resultat = -1;
        else
          if (colA.orderIndex > colB.orderIndex)
            resultat = 1;

        return resultat;  
      })
      .filter (colonne => (colonne as any).field !== undefined)
      .map ((colonne: ColumnBase) =>
      {
        donneesColonne = this.colonnes.find ((col) =>
                         {
                           return col.champ === (colonne as any).field;
                         });

        return donneesColonne!.identifiant;
      });

      this.ordresChange.emit (identifiantsColonnes);
    }, {injector: this.injector});
  }

  //Le groupe de colonne chage
  //  Paramètres
  //    groupes : Description des groupes
  public GroupeColonneChange (groupes: GroupDescriptor[]) : void 
  {
    let groupesColonnes: GroupeColonne[];
    let donneesColonne: DonneesColonne | undefined;
    let groupeColonne: GroupeColonne;

    this.groupes = groupes;

    groupesColonnes = groupes.map ((grp) =>
    {
      donneesColonne = this.colonnes.find ((col) =>
                       {
                         return col.champ === grp.field;
                       });

      groupeColonne = new GroupeColonne ();
      groupeColonne.identifiant = donneesColonne!.identifiant;
      groupeColonne.sens = grp.dir ? grp.dir : "asc";

      return groupeColonne;
    });

    this.groupeColonnesChange.emit (groupesColonnes);
  }

  //La visibilité des colonnes change
  //  Paramètres
  //    evenement : Evénement
  public VisibiliteColonneChange (evenement: ColumnVisibilityChangeEvent) : void 
  {
    let donneesColonne: DonneesColonne | undefined;
    let visibiliteColonnes: VisibiliteColonne[];
    let visibiliteColonne: VisibiliteColonne;

    visibiliteColonnes = evenement.columns.map ((col) =>
    {
      donneesColonne = this.colonnes.find ((colonne) =>
                       {
                         return colonne.champ === (col as any).field;
                       });

      donneesColonne!.visible = !col.hidden;

      visibiliteColonne = new VisibiliteColonne ();
      visibiliteColonne.identifiant = donneesColonne!.identifiant;
      visibiliteColonne.visible = !col.hidden;

      return visibiliteColonne;
    });

    this.visibiliteColonnesChange.emit (visibiliteColonnes);
  }

  //La taille des colonnes change
  //  Paramètres
  //    evenement : Evénement
  public TailleColonneChange (evenement: ColumnResizeArgs[]) : void 
  {
    let donneesColonne: DonneesColonne | undefined;
    let tailleColonne: TailleColonne;

    donneesColonne = this.colonnes.find ((col) =>
    {
      return col.champ === (evenement[0].column as any).field;
    });

    tailleColonne = new TailleColonne ();
    tailleColonne.identifiant = donneesColonne!.identifiant;
    tailleColonne.taille = Math.round (evenement[0].newWidth!);

    this.tailleColonneChange.emit (tailleColonne);
  }
}
